import React, { useEffect, useMemo, useState } from 'react';
import type {
  LeadDetail,
  RecordRetainerPaymentRequest,
  RequestRetainerRequest,
} from '../api/intakeCrmApi';

interface RetainerDeskModalProps {
  lead: LeadDetail;
  open: boolean;
  mode: 'request' | 'payment';
  onClose: () => void;
  onSubmitRequest: (payload: RequestRetainerRequest) => Promise<void>;
  onSubmitPayment: (payload: RecordRetainerPaymentRequest) => Promise<void>;
}

const billingBasisOptions: Array<{
  value: NonNullable<RequestRetainerRequest['billing_basis']>;
  label: string;
  description: string;
}> = [
  {
    value: 'trust_retainer',
    label: 'Trust Retainer',
    description: 'Collect the retainer into trust and apply it later against earned work.',
  },
  {
    value: 'flat_fee_advance',
    label: 'Flat Fee Advance',
    description: 'Use this when the firm treats the intake retainer as an advance fee arrangement.',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Use when the intake retainer is partly trust-funded and partly fee-based.',
  },
];

const paymentMethodOptions: Array<NonNullable<RecordRetainerPaymentRequest['payment_method']>> = [
  'ach',
  'credit_card',
  'check',
  'wire',
  'eft',
  'cash',
];

function formatCurrency(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function getDefaultDueDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
}

function getTodayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

const RetainerDeskModal: React.FC<RetainerDeskModalProps> = ({
  lead,
  open,
  mode,
  onClose,
  onSubmitRequest,
  onSubmitPayment,
}) => {
  const posture = lead.operationalProjection?.retainer_posture;
  const requestedAmount = posture?.requested_amount || 0;
  const collectedAmount = posture?.collected_amount || 0;
  const outstandingAmount = posture?.outstanding_amount || requestedAmount;

  const [requestAmount, setRequestAmount] = useState('');
  const [requestDueDate, setRequestDueDate] = useState('');
  const [requestMinimumToBegin, setRequestMinimumToBegin] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestBillingBasis, setRequestBillingBasis] = useState<NonNullable<RequestRetainerRequest['billing_basis']>>('trust_retainer');
  const [requestRequiredForWorkStart, setRequestRequiredForWorkStart] = useState(false);
  const [requestReplenishAmount, setRequestReplenishAmount] = useState('');

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<NonNullable<RecordRetainerPaymentRequest['payment_method']>>('ach');
  const [paymentReferenceNumber, setPaymentReferenceNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentEffectiveDate, setPaymentEffectiveDate] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setRequestAmount(requestedAmount > 0 ? requestedAmount.toFixed(2) : '');
    setRequestDueDate(posture?.due_date ? posture.due_date.slice(0, 10) : getDefaultDueDate());
    setRequestMinimumToBegin(
      typeof posture?.requested_amount === 'number' && posture.requested_amount > 0
        ? posture.requested_amount.toFixed(2)
        : '',
    );
    setRequestNotes(posture?.notes || '');
    setRequestBillingBasis('trust_retainer');
    setRequestRequiredForWorkStart(Boolean(posture?.reminder_needed));
    setRequestReplenishAmount('');

    setPaymentAmount(
      outstandingAmount > 0
        ? outstandingAmount.toFixed(2)
        : requestedAmount > 0
          ? requestedAmount.toFixed(2)
          : '',
    );
    setPaymentMethod('ach');
    setPaymentReferenceNumber('');
    setPaymentNotes('');
    setPaymentEffectiveDate(getTodayDate());
    setError(null);
  }, [open, posture?.due_date, posture?.notes, posture?.reminder_needed, posture?.requested_amount, outstandingAmount, requestedAmount]);

  const leadCaseTypes = useMemo(() => {
    const practiceAreas = lead.leadProfile?.practiceAreas?.length
      ? lead.leadProfile.practiceAreas
      : (lead.practiceArea || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
    return practiceAreas.length > 0 ? practiceAreas.join(', ') : 'Not classified yet';
  }, [lead.leadProfile?.practiceAreas, lead.practiceArea]);

  const modeTitle = mode === 'request' ? 'Request Intake Retainer' : 'Record Retainer Payment';
  const modeSubtitle = mode === 'request'
    ? 'Create a real intake-stage retainer obligation that follows the client into billing without blocking conversion.'
    : 'Post a real payment into trust now so the collected balance is available when the lead converts into a matter.';

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      if (mode === 'request') {
        const numericAmount = Number(requestAmount);
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
          throw new Error('Requested amount must be greater than zero.');
        }

        const minimumToBegin = requestMinimumToBegin.trim()
          ? Number(requestMinimumToBegin)
          : undefined;
        const replenishAmount = requestReplenishAmount.trim()
          ? Number(requestReplenishAmount)
          : undefined;

        await onSubmitRequest({
          requested_amount: numericAmount,
          due_date: requestDueDate || undefined,
          minimum_to_begin: Number.isFinite(minimumToBegin as number) ? minimumToBegin : undefined,
          notes: requestNotes.trim() || undefined,
          billing_basis: requestBillingBasis,
          required_for_work_start: requestRequiredForWorkStart,
          replenish_amount: Number.isFinite(replenishAmount as number) ? replenishAmount : undefined,
        });
      } else {
        const numericAmount = Number(paymentAmount);
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
          throw new Error('Payment amount must be greater than zero.');
        }

        await onSubmitPayment({
          amount: numericAmount,
          payment_method: paymentMethod,
          reference_number: paymentReferenceNumber.trim() || undefined,
          notes: paymentNotes.trim() || undefined,
          effective_date: paymentEffectiveDate || undefined,
        });
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save retainer workflow');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
      <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-300">
              Retainer Desk
            </p>
            <h3 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
              {modeTitle}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
              {modeSubtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid gap-6 px-6 py-5 lg:grid-cols-[1.2fr_0.9fr]">
          <div className="space-y-5">
            {mode === 'request' ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Requested Amount
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={requestAmount}
                      onChange={(event) => setRequestAmount(event.target.value)}
                      placeholder="2500.00"
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Due Date
                    </span>
                    <input
                      type="date"
                      value={requestDueDate}
                      onChange={(event) => setRequestDueDate(event.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Minimum To Begin Work
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={requestMinimumToBegin}
                      onChange={(event) => setRequestMinimumToBegin(event.target.value)}
                      placeholder="1500.00"
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Replenish Target
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={requestReplenishAmount}
                      onChange={(event) => setRequestReplenishAmount(event.target.value)}
                      placeholder="1000.00"
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Billing Basis
                  </span>
                  <select
                    value={requestBillingBasis}
                    onChange={(event) => setRequestBillingBasis(event.target.value as NonNullable<RequestRetainerRequest['billing_basis']>)}
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                  >
                    {billingBasisOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {billingBasisOptions.find((option) => option.value === requestBillingBasis)?.description}
                  </p>
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Intake Finance Note
                  </span>
                  <textarea
                    value={requestNotes}
                    onChange={(event) => setRequestNotes(event.target.value)}
                    rows={4}
                    placeholder="Explain what the retainer covers, what is due before work begins, and any special attorney instructions."
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                  />
                </label>

                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/60">
                  <input
                    type="checkbox"
                    checked={requestRequiredForWorkStart}
                    onChange={(event) => setRequestRequiredForWorkStart(event.target.checked)}
                    className="mt-1 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900 dark:text-white">
                      Mark as required before substantive work starts
                    </span>
                    <span className="block text-xs text-slate-600 dark:text-slate-400">
                      This does not block lead conversion, but it keeps the intake worker and checklist focused on collection follow-up.
                    </span>
                  </span>
                </label>
              </>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Payment Amount
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paymentAmount}
                      onChange={(event) => setPaymentAmount(event.target.value)}
                      placeholder="1500.00"
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Effective Date
                    </span>
                    <input
                      type="date"
                      value={paymentEffectiveDate}
                      onChange={(event) => setPaymentEffectiveDate(event.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Payment Method
                    </span>
                    <select
                      value={paymentMethod}
                      onChange={(event) => setPaymentMethod(event.target.value as NonNullable<RecordRetainerPaymentRequest['payment_method']>)}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    >
                      {paymentMethodOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Reference Number
                    </span>
                    <input
                      type="text"
                      value={paymentReferenceNumber}
                      onChange={(event) => setPaymentReferenceNumber(event.target.value)}
                      placeholder="Check #, ACH trace, or receipt ID"
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Payment Note
                  </span>
                  <textarea
                    value={paymentNotes}
                    onChange={(event) => setPaymentNotes(event.target.value)}
                    rows={4}
                    placeholder="Capture any collection context, confirmation details, or billing notes for the trust team."
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-emerald-900/40"
                  />
                </label>
              </>
            )}

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
                {error}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/60 dark:bg-emerald-950/30">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                Retainer Posture
              </p>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80">Requested</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{formatCurrency(requestedAmount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80">Collected</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{formatCurrency(collectedAmount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80">Outstanding</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{formatCurrency(outstandingAmount)}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <p>Status: {posture?.status ? posture.status.replace(/_/g, ' ') : 'not started'}</p>
                <p>Trust target: {posture?.trust_account_name || 'Firm default trust account'}</p>
                <p>Case types: {leadCaseTypes}</p>
                {posture?.due_date && <p>Due: {posture.due_date.slice(0, 10)}</p>}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Why this matters
              </p>
              <div className="mt-3 space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p>
                  The intake checklist can keep reminding the lawyer to collect the retainer without blocking lead conversion.
                </p>
                <p>
                  Payments recorded here land in trust now and can be allocated to the new matter automatically once the lead converts.
                </p>
                <p>
                  If there is still a balance due at conversion, the backend will create the real billing obligation on the matter instead of losing the intake finance trail.
                </p>
              </div>
            </div>

            {(posture?.trust_ledger_id || posture?.retainer_bill_number || posture?.retainer_bill_status) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Billing Linkage
                </p>
                <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
                  {posture?.trust_ledger_id && <p>Trust ledger: {posture.trust_ledger_id}</p>}
                  {posture?.retainer_bill_number && <p>Retainer bill: {posture.retainer_bill_number}</p>}
                  {posture?.retainer_bill_status && <p>Bill status: {posture.retainer_bill_status}</p>}
                  {posture?.allocated_matter_id && <p>Allocated matter: {posture.allocated_matter_id}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Uses the firm&rsquo;s default trust account when available. This workflow is fully replaceable by your future client-facing payment request experience.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving
                ? mode === 'request' ? 'Saving Request...' : 'Recording Payment...'
                : mode === 'request' ? 'Save Retainer Request' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetainerDeskModal;
